import {STATUS_CODES} from 'core/constants';
import {TAppDispatch, TAppRootState} from 'core/redux/types';
import {IBaseErrorResponse, IServerError} from '../types';
import {ServerHttpException} from 'core/exceptions/ServerHttpException';
import {IHttpClientResponse} from 'core/types';
import {NEXT_PUBLIC_API_HOST, NEXT_PUBLIC_LOGGER_TYPE} from 'core/helpers/env';
import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryMeta, QueryReturnValue} from '@reduxjs/toolkit/query';
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query/react';
import {Logger} from 'core/modules/logger';
import {isBrowserContext} from 'core/helpers';
import {getRequestCookieHeader} from 'core/helpers/cookie';
import {getClientIpFromContext} from 'core/helpers/clientIp';
import {getRequestId, REQUEST_ID_HEADER} from 'core/modules/request-id';

interface ICustomFetchArgs extends FetchArgs {
    onSuccess?: (
        dispatch: TAppDispatch,
        data: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
    ) => Promise<void>;
}

export type TCustomBaseQueryType = BaseQueryFn<
    string | ICustomFetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
>;

export const appendHeaders = (headers: Headers) => {
    const requestId = getRequestId();

    if (requestId && !headers.has(REQUEST_ID_HEADER)) {
        headers.set(REQUEST_ID_HEADER, requestId);
    }

    if (!isBrowserContext()) {
        if (!headers.has('X-Requested-With')) {
            headers.set('X-Requested-With', 'XMLHttpRequest');
        }

        const cookieHeader = getRequestCookieHeader();
        if (cookieHeader?.Cookie && !headers.has('Cookie')) {
            headers.set('Cookie', cookieHeader.Cookie);
        }

        headers.set('Referer', 'simplewine.ru');
        headers.set('Origin', 'simplewine.ru');

        // добавляем IP клиента в заголовки запроса
        const clientIp = getClientIpFromContext();

        if (clientIp) {
            headers.set('X-Real-IP', clientIp);
            headers.set('X-Forwarded-For', clientIp);
        }
    }

    return headers;
};

const baseQuery = fetchBaseQuery({
    baseUrl: NEXT_PUBLIC_API_HOST,
    prepareHeaders: (headers) => appendHeaders(headers),
    credentials: 'include',
});

// eslint-disable-next-line complexity
export const customBaseQuery: TCustomBaseQueryType = async (args, api, extraOptions) => {
    const state = api.getState() as TAppRootState;

    const logger = new Logger(NEXT_PUBLIC_LOGGER_TYPE || 'PLAIN_TEXT_CONSOLE', 'SSR');

    let result = await baseQuery(args, api, extraOptions);

    logger.log(result, state?.requestLogger?.pathname);

    if (result.error?.status === STATUS_CODES.UNAUTHORIZED) {
        return result;
    }

    if (result.error?.status === STATUS_CODES.INTERNAL_SERVER_ERROR) {
        throw new ServerHttpException(result.error?.status, result as IHttpClientResponse);
    }

    if (result.error && [STATUS_CODES.SERVICE_UNAVAILABLE].includes(result.error.status as number)) {
        return result;
    }

    if ('string' !== typeof args) {
        try {
            const {onSuccess} = args as ICustomFetchArgs;

            await onSuccess?.(api.dispatch, result);
        } catch (e) {
            console.error('Error in onSuccess method', e);
            throw e;
        }
    }

    return result;
};

export const transformErrorResponse = (response: FetchBaseQueryError): IServerError => {
    return {status: response.status, ...(response.data as IBaseErrorResponse)?.errors};
};
